/**
 * tradeSearchCopy — the Trade search helper's clipboard payload, plus the Dex
 * page copy button's smarter suffix (2026-08-28 ask).
 *
 * Turns one side of a comparison (the friend's missing leads) into a paste-
 * ready Pokémon GO search, e.g. "34,150,177,947&!traded" or, for the Shiny
 * Dex, "34,150&shiny&!traded". Pasted into GO it surfaces every Pokémon in
 * YOUR storage the friend still needs — which is why three rules apply on top
 * of the bare number list:
 *
 *   - UNTRADABLE SPECIES ARE DROPPED (Mew, Zygarde, Eternatus...): this list
 *     exists only to set up trades, and a species GO will never trade is a
 *     row the search should not show. isKnownUntradableSpecies fails closed,
 *     so an unreleased future species is never wrongly dropped.
 *   - THE VARIANT'S OWN TOKEN is appended where GO has one (shiny, xxl...),
 *     from tradeSearchString's shared table — so a Shiny Dex list only shows
 *     shinies. Lucky deliberately has NO token (a lucky is conferred BY the
 *     trade; your tradables are not lucky yet), and Perfect has none (IVs
 *     re-roll on arrival).
 *   - "!traded" always: a Pokémon can only be traded once, so copies received
 *     in a trade can never be sent on.
 *
 * Pure and tiny so the format is testable without the pane.
 */
import type { CompareVariant, LiveCompare, PanelRow } from "@/components/trade/compareTypes";
import { NATIONAL_DEX_TOTAL } from "@/lib/constants";
import { VARIANT_SEARCH_TOKEN } from "@/lib/tradeSearchString";
import {
  availableRosterForVariant,
  isKnownUntradableSpecies,
  isTradeGatedVariant,
} from "@/lib/variantAvailability";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";

/**
 * Unique dex numbers for one variant, ascending, GO-search formatted, with
 * the trade rules above applied.
 *
 * SPECIES-LEVEL LEADS ONLY (2026-08-29, the lucky Giratina report): a lead
 * still carrying a formId is a form-specific copy the friend lacks (their
 * Origin Forme, say) while their species-level row may well be caught. GO
 * search cannot express formes, so flattening those to a bare dex number
 * printed "487" for a friend who already owns a lucky Giratina. Form-level
 * differences stay in the lead panels, where the tile names the forme; this
 * list is only the species the friend is missing outright (the formId-null
 * lead, which exists exactly when their species-level row is absent).
 */
export function tradeSearchList(rows: readonly PanelRow[], variant: CompareVariant): string {
  const ids = new Set<number>();
  for (const r of rows) {
    if (r.variant !== variant || r.formId != null) continue;
    if (isKnownUntradableSpecies(r.id)) continue;
    ids.add(r.id);
  }
  if (ids.size === 0) return "";
  const numbers = [...ids].sort((a, b) => a - b).join(",");
  const token = VARIANT_SEARCH_TOKEN[variant] ?? null;
  return token ? `${numbers}&${token}&!traded` : `${numbers}&!traded`;
}

/** The GO search for a list of dex numbers, with the trade rules applied. */
function searchFor(ids: Iterable<number>, variant: CompareVariant): string {
  const numbers = [...ids].sort((a, b) => a - b).join(",");
  if (numbers === "") return "";
  const token = VARIANT_SEARCH_TOKEN[variant] ?? null;
  return token ? `${numbers}&${token}&!traded` : `${numbers}&!traded`;
}

/**
 * EVERYTHING the friend is missing from one dex, whether or not Nexus Dex
 * knows you hold it (2026-09-19 owner ask: "copy friend's missing numbers").
 *
 * tradeSearchList is the narrow answer: the leads Nexus Dex can already
 * match against your own dex. But a dex is only as complete as its scans,
 * and a box holds copies the dex never registered (an unscanned box, a
 * second account's spares). Pasting the friend's WHOLE gap list into GO
 * surfaces every one of those too. It is the species roster of the variant
 * minus what the friend holds at species level, with untradable species
 * dropped; the holdings come from the live comparison (theirs = bothHave
 * plus theyHave, minus Lucky reference rows, which are not holdings). Null
 * when the comparison has not loaded.
 */
export function friendMissingList(live: LiveCompare | undefined, variant: CompareVariant): string | null {
  if (!live) return null;
  const theirs = new Set<number>();
  for (const r of [...live.bothHave, ...live.theyHave]) {
    if (r.variant !== variant || r.formId != null || r.reference === true) continue;
    theirs.add(r.speciesId);
  }
  const roster = availableRosterForVariant(variant as PokedexVariant);
  const ids: number[] = [];
  if (roster) {
    for (const id of roster) if (!theirs.has(id) && !isKnownUntradableSpecies(id)) ids.push(id);
  } else {
    for (let id = 1; id <= NATIONAL_DEX_TOTAL; id += 1) {
      if (!theirs.has(id) && !isKnownUntradableSpecies(id)) ids.push(id);
    }
  }
  return searchFor(ids, variant);
}

/** How many dex numbers a search string carries (0 for the empty string). */
export function searchNumberCount(search: string): number {
  if (search === "") return 0;
  return search.split("&")[0].split(",").length;
}

/** The dex numbers in a search string, in the order they were written. */
export function searchNumbers(search: string): number[] {
  if (search === "") return [];
  return search.split("&")[0].split(",").map(Number).filter((n) => Number.isFinite(n));
}

/**
 * The Dex page copy button's string: the filtered grid's numbers, made a real
 * GO search rather than a bare list (2026-08-28 ask). The grid copy serves
 * more purposes than trading, so the rules are narrower than the trade list's:
 *
 *   - the variant's own token where GO has one ("&shiny" on the Shiny Dex,
 *     "&xxl" on XXL...), so pasting shows the right copies;
 *   - Lucky (and Shiny+Lucky) are TRADE-ONLY dexes: they gain "&!traded"
 *     (a received copy cannot be sent on) and drop untradable species — a
 *     lucky of a species GO will not trade cannot exist to be found.
 */
export function dexPageCopyString(
  variant: PokedexVariant,
  numbers: readonly number[],
): { text: string; count: number } {
  const tradeGated = isTradeGatedVariant(variant);
  const ids = tradeGated ? numbers.filter((n) => !isKnownUntradableSpecies(n)) : [...numbers];
  if (ids.length === 0) return { text: "", count: 0 };
  const tokens: string[] = [];
  const token = VARIANT_SEARCH_TOKEN[variant] ?? null;
  if (token) tokens.push(token);
  if (tradeGated) tokens.push("!traded");
  const suffix = tokens.length > 0 ? `&${tokens.join("&")}` : "";
  return { text: ids.join(",") + suffix, count: ids.length };
}
